import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ConfettiBurst } from "@/components/ConfettiBurst";
import { playDingSound } from "@/lib/sound";

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { refresh } = useAuth();
    const nav = useNavigate();
    const [status, setStatus] = useState("polling");
    const [progress, setProgress] = useState(8);
    const [confettiTrigger, setConfettiTrigger] = useState(0);
    const attemptsRef = useRef(0);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!sessionId) { setStatus("failed"); return; }
        const MAX = 45; // up to ~54s of polling at 1.2s intervals
        const poll = async () => {
            attemptsRef.current += 1;
            setProgress((p) => Math.min(95, p + 3));
            try {
                const res = await api.get(`/billing/checkout-status/${sessionId}`);
                if (res.data.payment_status === "paid" || res.data.applied) {
                    setStatus("paid");
                    setProgress(100);
                    setConfettiTrigger((c) => c + 1);
                    playDingSound();
                    await refresh();
                    return;
                }
                if (res.data.status === "expired" || res.data.payment_status === "failed") { setStatus("failed"); return; }
            } catch (e) {}
            if (attemptsRef.current >= MAX) { setStatus("timeout"); return; }
            timerRef.current = setTimeout(poll, 1200);
        };
        poll();
        return () => clearTimeout(timerRef.current);
    }, [sessionId, refresh]);

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-16 sm:py-24">
                <div className="max-w-xl mx-auto px-4">
                    <Card className="pp-bg-1 border-[#242436] text-white">
                        <CardContent className="p-8">
                            {status === "polling" && (
                                <div className="text-center" data-testid="billing-success-status">
                                    <Loader2 className="w-10 h-10 mx-auto text-[#a855f7] animate-spin" />
                                    <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-widest">PROCESSING PAYMENT...</h1>
                                    <p className="mt-1 text-sm text-[#b7b7c6]">Hang tight — unlocking your tier.</p>
                                    <Progress value={progress} className="mt-4 h-2 bg-[#1b1b26]" />
                                </div>
                            )}
                            {status === "paid" && (
                                <div className="text-center" data-testid="billing-success-status">
                                    <CheckCircle2 className="w-12 h-12 mx-auto text-[#22c55e]" />
                                    <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-widest neon-text-green">PAYMENT CONFIRMED</h1>
                                    <p className="mt-1 text-sm text-[#b7b7c6]">Welcome to HOTSLIP. Your tier is active.</p>
                                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                        <Button onClick={() => nav("/")} className="bg-[#22c55e] hover:brightness-110 text-[#07130b] font-semibold" data-testid="billing-success-continue-button">View Today’s Picks</Button>
                                        <Button asChild variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white">
                                            <Link to="/account">My Account</Link>
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {status === "timeout" && (
                                <div className="text-center" data-testid="billing-success-status">
                                    <Clock className="w-10 h-10 mx-auto text-[#f97316]" />
                                    <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-widest">STILL PROCESSING</h1>
                                    <p className="mt-1 text-sm text-[#b7b7c6]">Your payment is taking longer than expected. Once Stripe confirms, your tier will unlock automatically.</p>
                                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                        <Button onClick={() => nav("/account")} className="bg-[#6f3fec] hover:brightness-110 text-white">Go to Account</Button>
                                        <Button onClick={() => window.location.reload()} variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white">Refresh Status</Button>
                                    </div>
                                </div>
                            )}
                            {status === "failed" && (
                                <div className="text-center" data-testid="billing-success-status">
                                    <XCircle className="w-10 h-10 mx-auto text-[#ef4444]" />
                                    <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-widest">PAYMENT NOT COMPLETED</h1>
                                    <p className="mt-1 text-sm text-[#b7b7c6]">Looks like the checkout was canceled or failed.</p>
                                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                        <Button asChild className="bg-[#6f3fec] hover:brightness-110 text-white"><Link to="/pricing">Try Again</Link></Button>
                                        <Button asChild variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white"><Link to="/">Back to Home</Link></Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </section>
            <Footer />
            <ConfettiBurst trigger={confettiTrigger} />
        </div>
    );
}
